import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  membershipApi,
  type MembershipDetail,
  type MembershipIssueInput,
  type MembershipMemberRow,
  type MembershipPointsAdjustInput,
  type MembershipPointsEarnInput,
  type MembershipPointsRedeemInput,
  type MembershipTypeRow,
  type MembershipTypeUpsertRequest,
  type Paginated,
} from './api/membership.api';

export const membershipKeys = {
  all: ['membership'] as const,
  analytics: () => [...membershipKeys.all, 'analytics'] as const,
  kpis: () => [...membershipKeys.all, 'kpis'] as const,
  typesWithStats: () => [...membershipKeys.all, 'types', 'with-stats'] as const,
  types: () => [...membershipKeys.all, 'types'] as const,
  type: (typeUuid: string) => [...membershipKeys.all, 'types', typeUuid] as const,
  nameAvailability: (name: string, excludeTypeUuid?: string) => [...membershipKeys.all, 'name-availability', name, excludeTypeUuid || ''] as const,
  members: (typeUuid: string, status: string, query: string, page: number, pageSize: number) => (
    [...membershipKeys.all, 'members', typeUuid, status, query, page, pageSize] as const
  ),
  memberSummary: (membershipUuid: string) => [...membershipKeys.all, 'member-summary', membershipUuid] as const,
  draft: () => [...membershipKeys.all, 'draft'] as const,
  userLookup: (query: string) => [...membershipKeys.all, 'user-lookup', query] as const,
  membership: (membershipUuid: string) => [...membershipKeys.all, 'membership', membershipUuid] as const,
  ledger: (membershipUuid: string, eventType: string, fromInstant: string, toInstant: string, page: number, pageSize: number) => (
    [...membershipKeys.all, 'ledger', membershipUuid, eventType, fromInstant, toInstant, page, pageSize] as const
  ),
  recentActivity: () => [...membershipKeys.all, 'activity-recent'] as const,
  expiringMembers: (withinDays: number) => [...membershipKeys.all, 'expiring', withinDays] as const,
  upgradeCandidates: () => [...membershipKeys.all, 'upgrade-candidates'] as const,
  pendingRequests: () => [...membershipKeys.all, 'pending-requests'] as const,
};

type MembershipTypesPage = Paginated<MembershipTypeRow>;

const invalidateMemberCollections = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: [...membershipKeys.all, 'members'] });
};

const invalidateMembershipOverview = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: membershipKeys.types() });
  queryClient.invalidateQueries({ queryKey: membershipKeys.analytics() });
  queryClient.invalidateQueries({ queryKey: membershipKeys.kpis() });
  queryClient.invalidateQueries({ queryKey: membershipKeys.typesWithStats() });
  invalidateMemberCollections(queryClient);
  queryClient.invalidateQueries({ queryKey: membershipKeys.recentActivity() });
  queryClient.invalidateQueries({ queryKey: [...membershipKeys.all, 'expiring'] });
  queryClient.invalidateQueries({ queryKey: membershipKeys.upgradeCandidates() });
};

const invalidateMembershipDetail = (
  queryClient: ReturnType<typeof useQueryClient>,
  membershipUuid: string
) => {
  queryClient.invalidateQueries({ queryKey: membershipKeys.membership(membershipUuid) });
  queryClient.invalidateQueries({ queryKey: [...membershipKeys.all, 'ledger', membershipUuid] });
};

export const useMembershipAnalytics = (enabled = true) => useQuery({
  queryKey: membershipKeys.analytics(),
  queryFn: () => membershipApi.getAnalytics(),
  refetchInterval: 60_000,
  enabled
});

export const useMembershipKpis = (enabled = true) => useQuery({
  queryKey: membershipKeys.kpis(),
  queryFn: () => membershipApi.getKpis(),
  refetchInterval: 60_000,
  enabled,
});

export const useMembershipTypes = (enabled = true) => useQuery({
  queryKey: membershipKeys.types(),
  queryFn: () => membershipApi.getTypes(),
  enabled
});

export const useMembershipTypesWithStats = (enabled = true) => useQuery({
  queryKey: membershipKeys.typesWithStats(),
  queryFn: () => membershipApi.getTypesWithStats(),
  enabled,
});

export const usePendingMembershipRequests = (enabled = true) => useQuery({
  queryKey: membershipKeys.pendingRequests(),
  queryFn: () => membershipApi.listPendingRequests(),
  enabled,
});

export const useActivateMembershipRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestUuid, code }: { requestUuid: string; code: string }) =>
      membershipApi.activateRequest(requestUuid, code),
    onSuccess: () => {
      // The request leaves the pending list and a new member now exists.
      queryClient.invalidateQueries({ queryKey: membershipKeys.pendingRequests() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.analytics() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.kpis() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.typesWithStats() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.recentActivity() });
    },
  });
};

export const useMembershipType = (typeUuid?: string, enabled = true) => useQuery({
  queryKey: membershipKeys.type(String(typeUuid || 'new')),
  queryFn: () => membershipApi.getType(String(typeUuid)),
  enabled: Boolean(typeUuid) && enabled
});

export const useMembershipTypeNameAvailability = (
  name: string,
  excludeTypeUuid?: string,
  enabled = true
) => useQuery({
  queryKey: membershipKeys.nameAvailability(name, excludeTypeUuid),
  queryFn: () => membershipApi.checkTypeNameAvailability(name, excludeTypeUuid),
  enabled: enabled && Boolean(String(name || '').trim()),
  staleTime: 30_000
});

export const useMembershipMembersByType = ({
  typeUuid,
  status,
  query,
  page,
  pageSize,
  enabled = true
}: {
  typeUuid?: string;
  status?: string;
  query?: string;
  page: number;
  pageSize: number;
  enabled?: boolean;
}) => useQuery({
  queryKey: membershipKeys.members(String(typeUuid || ''), String(status || ''), String(query || ''), page, pageSize),
  queryFn: () => membershipApi.getMembersByType({
    typeUuid: typeUuid || undefined,
    status: status || undefined,
    query: query || undefined,
    page,
    pageSize
  }),
  enabled
});

export const useMemberSummary = (membershipUuid?: string, enabled = true) => useQuery({
  queryKey: membershipKeys.memberSummary(String(membershipUuid || '')),
  queryFn: () => membershipApi.getMemberSummary(String(membershipUuid)),
  enabled: Boolean(membershipUuid) && enabled,
});

export const useMembershipUserLookup = (query: string, enabled = true) => useQuery({
  queryKey: membershipKeys.userLookup(query),
  queryFn: () => membershipApi.searchUsersByPhone(query),
  enabled: enabled && Boolean(String(query || '').trim()),
  staleTime: 30_000
});

export const useMembership = (membershipUuid?: string, enabled = true) => useQuery({
  queryKey: membershipKeys.membership(String(membershipUuid || '')),
  queryFn: () => membershipApi.getMembership(String(membershipUuid)),
  enabled: Boolean(membershipUuid) && enabled
});

export const useMembershipLedger = ({
  membershipUuid,
  eventType,
  fromInstant,
  toInstant,
  page,
  pageSize,
  enabled = true
}: {
  membershipUuid?: string;
  eventType?: string;
  fromInstant?: string;
  toInstant?: string;
  page: number;
  pageSize: number;
  enabled?: boolean;
}) => useQuery({
  queryKey: membershipKeys.ledger(
    String(membershipUuid || ''),
    String(eventType || ''),
    String(fromInstant || ''),
    String(toInstant || ''),
    page,
    pageSize
  ),
  queryFn: () => membershipApi.getMembershipLedger({
    membershipUuid: String(membershipUuid),
    eventType: eventType || undefined,
    fromInstant: fromInstant || undefined,
    toInstant: toInstant || undefined,
    page,
    pageSize
  }),
  enabled: Boolean(membershipUuid) && enabled
});

export const useActivateMembershipType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (typeUuid: string) => membershipApi.activateType(typeUuid),
    onMutate: async (typeUuid) => {
      await queryClient.cancelQueries({ queryKey: membershipKeys.types() });
      const previous = queryClient.getQueryData<MembershipTypesPage>(membershipKeys.types());
      queryClient.setQueryData<MembershipTypesPage | undefined>(membershipKeys.types(), (current) => {
        if (!current?.items) return current;
        return {
          ...current,
          items: current.items.map((item) => (
            item.membershipTypeUuid === typeUuid ? { ...item, isActive: true } : item
          ))
        };
      });
      return { previous };
    },
    onError: (_error, _typeUuid, context) => {
      if (context?.previous) {
        queryClient.setQueryData(membershipKeys.types(), context.previous);
      }
    },
    onSettled: () => {
      invalidateMembershipOverview(queryClient);
    }
  });
};

export const useDeactivateMembershipType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (typeUuid: string) => membershipApi.deactivateType(typeUuid),
    onMutate: async (typeUuid) => {
      await queryClient.cancelQueries({ queryKey: membershipKeys.types() });
      const previous = queryClient.getQueryData<MembershipTypesPage>(membershipKeys.types());
      queryClient.setQueryData<MembershipTypesPage | undefined>(membershipKeys.types(), (current) => {
        if (!current?.items) return current;
        return {
          ...current,
          items: current.items.map((item) => (
            item.membershipTypeUuid === typeUuid ? { ...item, isActive: false } : item
          ))
        };
      });
      return { previous };
    },
    onError: (_error, _typeUuid, context) => {
      if (context?.previous) {
        queryClient.setQueryData(membershipKeys.types(), context.previous);
      }
    },
    onSettled: () => {
      invalidateMembershipOverview(queryClient);
    }
  });
};

export const useDeleteMembershipType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (typeUuid: string) => membershipApi.deleteType(typeUuid),
    onSuccess: () => {
      invalidateMembershipOverview(queryClient);
    }
  });
};

export const useCreateMembershipType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: membershipApi.createType,
    onSuccess: () => {
      invalidateMembershipOverview(queryClient);
    }
  });
};

export const useUpdateMembershipType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ typeUuid, input }: { typeUuid: string; input: MembershipTypeUpsertRequest }) => membershipApi.updateType(typeUuid, input),
    onSuccess: (_result, variables) => {
      invalidateMembershipOverview(queryClient);
      queryClient.invalidateQueries({ queryKey: membershipKeys.type(variables.typeUuid) });
    }
  });
};

export const useIssueMembership = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MembershipIssueInput) => membershipApi.issueMembership(input),
    onSuccess: (result) => {
      invalidateMembershipOverview(queryClient);
      invalidateMembershipDetail(queryClient, result.membershipUuid);
    }
  });
};

export const useIssueWalkInMembership = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: membershipApi.issueWalkInMembership,
    onSuccess: (result) => {
      invalidateMembershipOverview(queryClient);
      invalidateMembershipDetail(queryClient, result.membership.membershipUuid);
    }
  });
};

export const useMembershipDraftQuery = (enabled = true) => useQuery({
  queryKey: membershipKeys.draft(),
  queryFn: () => membershipApi.getMembershipDraft(),
  enabled,
  staleTime: 0,
});

export const useSaveMembershipDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { stepReached: number; payload: Record<string, unknown> }) =>
      membershipApi.saveMembershipDraft(input),
    onSuccess: (result) => {
      queryClient.setQueryData(membershipKeys.draft(), result);
    },
  });
};

export const useDiscardMembershipDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => membershipApi.discardMembershipDraft(),
    onSuccess: () => {
      queryClient.setQueryData(membershipKeys.draft(), null);
    },
  });
};

/**
 * Splice a freshly-issued member row into every members-list query whose
 * filters this row would naturally appear in. Avoids the broad
 * invalidate-and-refetch that previously hit the per-path rate limit. Falls
 * back silently when `memberRow` is null — the caller should pair this with
 * a tier-stats invalidation so counts stay current.
 */
const spliceNewMemberRow = (
  queryClient: ReturnType<typeof useQueryClient>,
  memberRow: MembershipMemberRow | null,
) => {
  if (!memberRow) return false;
  let spliced = false;
  const allMembers = queryClient.getQueriesData<Paginated<MembershipMemberRow> | undefined>({
    queryKey: [...membershipKeys.all, 'members'],
  });
  for (const [queryKey, page] of allMembers) {
    // queryKey shape: [...all, 'members', typeUuid, status, query, pageNumber, pageSize]
    const typeUuid = String(queryKey[2] ?? '');
    const status = String(queryKey[3] ?? '');
    const query = String(queryKey[4] ?? '');
    const pageNumber = Number(queryKey[5] ?? 0);
    if (pageNumber !== 0) continue;
    if (query !== '') continue; // can't reliably predict whether the row matches a free-text search
    if (typeUuid !== '' && typeUuid !== memberRow.membershipTypeUuid) continue;
    if (status !== '' && status !== 'ACTIVE' && status !== 'EXPIRING') continue;
    if (!page) continue;
    const alreadyPresent = page.items?.some(
      (item) => item.membershipUuid === memberRow.membershipUuid,
    );
    if (alreadyPresent) continue;
    queryClient.setQueryData(queryKey, {
      ...page,
      items: [memberRow, ...(page.items ?? [])],
      totalElements: (page.totalElements ?? 0) + 1,
    });
    spliced = true;
  }
  return spliced;
};

const patchRevokedMemberRows = (
  queryClient: ReturnType<typeof useQueryClient>,
  membershipUuid: string,
) => {
  const allMembers = queryClient.getQueriesData<Paginated<MembershipMemberRow> | undefined>({
    queryKey: [...membershipKeys.all, 'members'],
  });
  for (const [queryKey, page] of allMembers) {
    if (!page?.items?.some((item) => item.membershipUuid === membershipUuid)) continue;
    const status = String(queryKey[3] ?? '');
    const removeFromPage = status === 'ACTIVE' || status === 'EXPIRING';
    const nextItems = removeFromPage
      ? page.items.filter((item) => item.membershipUuid !== membershipUuid)
      : page.items.map((item) => (
        item.membershipUuid === membershipUuid
          ? { ...item, status: 'REVOKED' }
          : item
      ));
    queryClient.setQueryData(queryKey, {
      ...page,
      items: nextItems,
      totalElements: removeFromPage ? Math.max(0, (page.totalElements ?? 0) - 1) : page.totalElements,
    });
  }
};

export const useIssueMembershipByPhone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { phone: string; tierUuid: string; startsAt?: string }) =>
      membershipApi.issueMembershipByPhone(input),
    onSuccess: (result) => {
      // Refresh tier-level stats / KPIs (counts must reflect the new member).
      queryClient.invalidateQueries({ queryKey: membershipKeys.types() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.analytics() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.kpis() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.typesWithStats() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.recentActivity() });
      invalidateMembershipDetail(queryClient, result.membership.membershipUuid);

      // Splice the new row into the members-list cache instead of
      // invalidating; if the backend couldn't assemble a row, fall back
      // to the broad invalidation path so the user still sees their
      // freshly-issued member.
      const spliced = spliceNewMemberRow(queryClient, result.memberRow);
      if (!spliced) {
        invalidateMemberCollections(queryClient);
      }
    },
  });
};

export const useRevokeMembership = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipUuid, reason }: { membershipUuid: string; reason: string }) =>
      membershipApi.revokeMembership(membershipUuid, reason),
    onMutate: async (variables) => {
      const detailKey = membershipKeys.membership(variables.membershipUuid);
      await queryClient.cancelQueries({ queryKey: detailKey });
      await queryClient.cancelQueries({ queryKey: [...membershipKeys.all, 'members'] });

      const previousDetail = queryClient.getQueryData<MembershipDetail>(detailKey);
      const previousMemberPages = queryClient.getQueriesData<Paginated<MembershipMemberRow> | undefined>({
        queryKey: [...membershipKeys.all, 'members'],
      });
      const revokedAt = new Date().toISOString();

      queryClient.setQueryData<MembershipDetail | undefined>(detailKey, (current) => (
        current
          ? {
            ...current,
            status: 'REVOKED',
            revokedReason: variables.reason,
            revokedAt,
            pointsBalance: 0,
          }
          : current
      ));
      patchRevokedMemberRows(queryClient, variables.membershipUuid);

      return { previousDetail, previousMemberPages };
    },
    onError: (_error, variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(membershipKeys.membership(variables.membershipUuid), context.previousDetail);
      }
      for (const [queryKey, page] of context?.previousMemberPages ?? []) {
        queryClient.setQueryData(queryKey, page);
      }
    },
    onSuccess: (result, variables) => {
      invalidateMembershipOverview(queryClient);
      invalidateMembershipDetail(queryClient, variables.membershipUuid);
      queryClient.setQueryData(membershipKeys.membership(variables.membershipUuid), result);
    }
  });
};

export const useRenewMembership = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipUuid, validityDaysOverride }: { membershipUuid: string; validityDaysOverride?: number }) =>
      membershipApi.renewMembership(membershipUuid, validityDaysOverride),
    onSuccess: (result, variables) => {
      invalidateMembershipOverview(queryClient);
      invalidateMembershipDetail(queryClient, variables.membershipUuid);
      queryClient.setQueryData(membershipKeys.membership(variables.membershipUuid), result);
    }
  });
};

export const useEarnPoints = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MembershipPointsEarnInput) => membershipApi.earnPoints(input),
    onSuccess: (_result, variables) => {
      invalidateMembershipDetail(queryClient, variables.membershipUuid);
      queryClient.invalidateQueries({ queryKey: membershipKeys.analytics() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.recentActivity() });
      queryClient.invalidateQueries({ queryKey: [...membershipKeys.all, 'expiring'] });
    }
  });
};

export const useRedeemPoints = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MembershipPointsRedeemInput) => membershipApi.redeemPoints(input),
    onSuccess: (_result, variables) => {
      invalidateMembershipDetail(queryClient, variables.membershipUuid);
      queryClient.invalidateQueries({ queryKey: membershipKeys.analytics() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.recentActivity() });
      queryClient.invalidateQueries({ queryKey: [...membershipKeys.all, 'expiring'] });
    }
  });
};

export const useRecentMembershipActivity = (enabled = true) => useQuery({
  queryKey: membershipKeys.recentActivity(),
  queryFn: () => membershipApi.getRecentActivity(20),
  refetchInterval: 60_000,
  enabled
});

export const useExpiringMembers = (withinDays = 30, enabled = true) => useQuery({
  queryKey: membershipKeys.expiringMembers(withinDays),
  queryFn: () => membershipApi.getExpiringMembers(withinDays),
  refetchInterval: 5 * 60_000,
  enabled
});

export const useUpgradeCandidates = (thresholdPct = 80, enabled = true) => useQuery({
  queryKey: [...membershipKeys.upgradeCandidates(), thresholdPct] as const,
  queryFn: () => membershipApi.getUpgradeCandidates(thresholdPct),
  refetchInterval: 5 * 60_000,
  enabled,
});

export const useBroadcastPreview = () => useMutation({
  mutationFn: (audience: string) => membershipApi.previewBroadcast(audience),
});

export const useMembershipBroadcast = () => useMutation({
  mutationFn: membershipApi.broadcast
});

export const useAdjustMembershipPoints = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MembershipPointsAdjustInput) => membershipApi.adjustPoints(input),
    onSuccess: (_result, variables) => {
      invalidateMembershipDetail(queryClient, variables.membershipUuid);
      queryClient.invalidateQueries({ queryKey: membershipKeys.analytics() });
      queryClient.invalidateQueries({ queryKey: membershipKeys.recentActivity() });
      queryClient.invalidateQueries({ queryKey: [...membershipKeys.all, 'expiring'] });
    }
  });
};
